import { lexer, marked, walkTokens } from 'marked';
import TurndownService from 'turndown';
import sanitizeHtml from 'sanitize-html';

marked.setOptions({ gfm: true });

const turndownService = new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced' });
const EMPTY_EDITOR_HTML_VALUES = new Set(['', '<p><br></p>', '<p><br /></p>', '<p></p>']);

const allowedTags = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'ul',
  'ol',
  'li',
  'a',
  'pre',
  'code',
  'h1',
  'h2',
  'h3',
  'blockquote'
];
const allowedAttributes = {
  a: ['href', 'title'],
  code: ['class']
};
const sanitizeOptions = {
  allowedTags,
  allowedAttributes,
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  allowedSchemesAppliedToAttributes: ['href'],
  allowProtocolRelative: false
};

export const normalizeMarkdownValue = (value) =>
  String(value ?? '')
    .replace(/\u200B/g, '')
    .trim();

const sanitizeRichTextHtml = (html) => sanitizeHtml(String(html ?? ''), sanitizeOptions).trim();

export const markdownToHtml = (markdown) => {
  const normalizedMarkdown = normalizeMarkdownValue(markdown);
  if (!normalizedMarkdown) return '';

  const raw = marked.parse(normalizedMarkdown);
  return sanitizeRichTextHtml(raw);
};

export const htmlToMarkdown = (html) => {
  if (!html) return '';

  const sanitizedHtml = sanitizeRichTextHtml(html);
  if (EMPTY_EDITOR_HTML_VALUES.has(sanitizedHtml)) return '';

  return normalizeMarkdownValue(turndownService.turndown(sanitizedHtml));
};

const plainTextFromMarkdown = (markdown) => {
  const normalized = normalizeMarkdownValue(markdown);
  if (!normalized) return '';

  const tokens = lexer(normalized);
  const parts = [];
  walkTokens(tokens, (token) => {
    switch (token.type) {
      case 'text':
      case 'escape':
        // List items wrap content in a parent `text` that duplicates the leaf `text` (marked quirk).
        if (token.tokens?.length) break;
        parts.push(token.text ?? '');
        break;
      case 'codespan':
      case 'code':
        parts.push(token.text ?? '');
        break;
      case 'br':
      case 'space':
        parts.push(' ');
        break;
      case 'html':
        if (token.text) parts.push(token.text);
        break;
      default:
        break;
    }
  });
  return parts.join('');
};

export const getRichTextVisibleLength = (markdown) =>
  plainTextFromMarkdown(markdown).replace(/\s+/g, ' ').trim().length;
